var _DataStub_objCachedChildStub;
import { __classPrivateFieldGet, __classPrivateFieldSet, __decorate } from "tslib";
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-multi-assign */
import omit from 'lodash-es/omit';
import { defaults } from './defaults';
import getPatchedDataSchema from './getPatchedDataSchema';
import { isNullish, isUndefined } from './utils/isNonNullable';
import { joinPath, toPathParts } from './utils/path';
import { processMaybeExpr, tryEvalExpr } from './utils/eval';
import { LazyGetter } from './utils/defineLazyGetter';
const defaultLoremFunction = (schema) => {
    if (schema.type === 'string')
        return 'Lorem Ipsum';
    if (schema.type === 'integer')
        return 123;
    if (schema.type === 'number')
        return 123;
    if (schema.type === 'boolean')
        return true;
    return null;
};
export class DataStub {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g;
        this.watchers = [];
        /** @internal 对象的字段 Stub 缓存 */
        _DataStub_objCachedChildStub.set(this, void 0);
        this.context = (_c = (_a = options.context) !== null && _a !== void 0 ? _a : (_b = options.parent) === null || _b === void 0 ? void 0 : _b.context) !== null && _c !== void 0 ? _c : defaults.context;
        this.exprContext = (_d = options.exprContext) !== null && _d !== void 0 ? _d : (_e = options.parent) === null || _e === void 0 ? void 0 : _e.exprContext;
        // 准备基础逻辑
        this.parent = options.parent;
        this.isRootStub = !this.parent;
        // 准备 schema
        this.id = this.isRootStub ? undefined : options.id;
        this.schema = Object.freeze(getPatchedDataSchema(options.schema)); // 加 freeze 避免 Vue 的奇葩问题
        if (this.isRootStub) {
            // 如果原来的 schema 有 id，那么就删除他！
            if (!isNullish(this.schema.id))
                this.schema = omit(this.schema, ['id']);
        }
        else {
            // 不是根节点的话，必须要有 id
            /* istanbul ignore next */
            if (isNullish(this.id))
                throw new Error('id is required for sub DataStub');
            // 如果原来的 schema id 不对，那么就改
            if (this.schema.id !== this.id)
                this.schema = Object.assign(Object.assign({}, this.schema), { id: this.id });
        }
        this.type = this.schema.type;
        // 准备特殊的 flags
        this.isVolatile = !!((!this.schema.enum && this.schema.enumExpr) || // 带动态枚举的，肯定 volatile
            (this.type === 'object' && // 带字段条件的，肯定 volatile
                (((_f = this.schema.groups) === null || _f === void 0 ? void 0 : _f.some((x) => x.condition)) || this.definedFields.some((x) => x.condition))));
        // 准备 path
        if (this.parent) {
            /* istanbul ignore if */
            if (options.pathParts)
                throw new Error('cannot set pathParts if DataStub has a parent');
            this.pathParts = this.parent.pathParts.slice();
            if (!this.isRootStub)
                this.pathParts.push(this.id);
        }
        else {
            this.pathParts = ((_g = options.pathParts) === null || _g === void 0 ? void 0 : _g.slice()) || [];
        }
        this.path = joinPath(this.pathParts);
        if (this.isRootStub) {
            // 根 DataStub
            // 此时的 id 肯定为空
            this.root = this;
            this.internalRootValue = options.value;
        }
        else {
            // 子节点 DataStub
            this.root = this.parent.root;
        }
    }
    // #region 基本能力
    getValue() {
        if (this.isRootStub)
            return this.internalRootValue;
        const { pathParts, root } = this;
        let ptr = root.value;
        for (let i = root.pathParts.length; i < pathParts.length; i++) {
            if (typeof ptr !== 'object' || ptr === null)
                break;
            const k = pathParts[i];
            ptr = ptr[k];
        }
        return ptr;
    }
    setValue(value) {
        this.handleSelfMutation(this.value, value, []);
    }
    get value() {
        return this.getValue();
    }
    set value(v) {
        this.setValue(v);
    }
    /**
     * @deprecated 直接取 .schema 也是可以的
     */
    /* istanbul ignore next */
    getSchema() {
        return this.schema;
    }
    /**
     * 处理子字段冒泡上来的修改
     */
    handleBubbledMutation(trace) {
        // assert(this.type === 'array' || this.type === 'object')
        // assert(trace.length >= 1)
        const { immutable } = this.context;
        const lastMut = trace[trace.length - 1];
        const id = lastMut.id;
        const oldValue = this.value;
        const oldValueIsNullish = !oldValue;
        if (immutable) {
            // clone is new value
            // eslint-disable-next-line no-nested-ternary
            const clone = this.type === 'array' ? (oldValueIsNullish ? [] : [...oldValue]) : Object.assign({}, oldValue);
            clone[id] = lastMut.value;
            this.handleSelfMutation(oldValue, clone, trace);
        }
        else {
            // clone is old value
            // eslint-disable-next-line no-nested-ternary
            const clone = oldValueIsNullish ? oldValue : this.type === 'array' ? [...oldValue] : Object.assign({}, oldValue);
            if (clone)
                clone[id] = lastMut.oldValue;
            // 处理子树有 null 的情况
            let midValue = oldValue;
            if (oldValueIsNullish) {
                if (this.type === 'array')
                    midValue = [];
                else
                    midValue = {};
            }
            if (midValue[id] !== lastMut.value) {
                this.context.set(midValue, id, lastMut.value);
            }
            this.handleSelfMutation(clone, midValue, trace);
        }
    }
    /**
     * 处理自己的修改
     */
    handleSelfMutation(oldValue, newValue, prevTrace) {
        const trace = [
            ...prevTrace,
            {
                oldValue,
                value: newValue,
                id: this.id,
                path: this.path,
                pathParts: this.pathParts,
            },
        ];
        // 如果不是根节点，需要向上冒泡
        if (!this.isRootStub) {
            this.parent.handleBubbledMutation(trace);
            return;
        }
        // 如果是根节点，就可以广播修改了
        this.internalRootValue = newValue;
        this.watchers.forEach((watcher) => {
            const { pathParts } = watcher;
            const trigged = 
            // 这里不做严格判断类型，因为可能存在监听数组时， 数字 2 和 字符串 2 之间导致的问题
            // eslint-disable-next-line eqeqeq
            pathParts.length <= trace[0].pathParts.length && pathParts.every((p, i) => p == trace[0].pathParts[i]);
            if (!trigged)
                return;
            const zDelta = trace[0].pathParts.length - pathParts.length;
            const tracePt = trace[zDelta];
            watcher.callback(tracePt.value, tracePt.oldValue, trace);
            if (watcher.once)
                watcher.remove();
        });
    }
    /**
     * 获取 **直接** 子元素的 DataStub（仅当前元素是 object / array 的时候有用）
     *
     * @remarks 如果你想通过 `"foo.bar[3]"` 这种 dataPath 取深层子元素，请使用 `getStubAtPath()` 方法
     */
    getChildStub(id) {
        switch (this.type) {
            case 'object': {
                if (!__classPrivateFieldGet(this, _DataStub_objCachedChildStub, "f"))
                    __classPrivateFieldSet(this, _DataStub_objCachedChildStub, {}, "f");
                if (id in __classPrivateFieldGet(this, _DataStub_objCachedChildStub, "f"))
                    return __classPrivateFieldGet(this, _DataStub_objCachedChildStub, "f")[id];
                const { definedFields, additionalFields } = this;
                const indexInDefinedFields = definedFields.findIndex((x) => x.id === id);
                const fieldSchema = indexInDefinedFields >= 0 ? definedFields[indexInDefinedFields] : additionalFields;
                if (!fieldSchema)
                    return null;
                const stub = new DataStub({ schema: fieldSchema, parent: this, id });
                if (indexInDefinedFields >= 0 && stub)
                    __classPrivateFieldGet(this, _DataStub_objCachedChildStub, "f")[id] = stub;
                return stub;
            }
            case 'array': {
                const itemSchema = this.schema.items;
                const index = +id;
                if (Number.isNaN(index) || id < 0)
                    return null;
                return new DataStub({ schema: itemSchema, parent: this, id: index });
            }
            default: {
                return null;
            }
        }
    }
    /**
     * 获取某个路径指向的元素的 DataStub
     *
     * @param path 路径，例如 `"foo.tags[3].type"` 或者 `["foo","tags",3,"type"]`
     */
    getStubAtPath(path) {
        let ptr = this;
        const parts = toPathParts(path);
        while (ptr && parts.length) {
            ptr = ptr.getChildStub(parts.shift());
        }
        return ptr;
    }
    /**
     * 修改一个子元素的值
     *
     * （仅当前元素是 object / array 的时候有用）
     *
     * @deprecated 现在改名叫 set 了
     */
    setChildValue(key, childValue, trace) {
        const subStub = this.getChildStub(key);
        subStub.handleSelfMutation(subStub.value, childValue, trace || []);
    }
    /**
     * 监听某个字段的变化
     *
     * @returns 一个函数，调用他可以停止监听
     */
    watch(path, callback, { once = false } = {}) {
        const { root } = this;
        const pathParts = [...this.pathParts, ...toPathParts(path)];
        const remove = () => {
            const idx = root.watchers.indexOf(it);
            /* istanbul ignore else */
            if (idx >= 0)
                root.watchers.splice(idx, 1);
        };
        const it = {
            pathParts,
            callback,
            once,
            remove,
        };
        root.watchers.push(it);
        return remove;
    }
    /**
     * 生成默认值
     */
    getDefaultValue(opts) {
        var _a;
        const { schema } = this;
        if (!isUndefined(schema.default)) {
            return processMaybeExpr(schema.default, this, this.exprContext);
        }
        // 有枚举就走枚举
        const enums = this.getEnums();
        if (enums === null || enums === void 0 ? void 0 : enums[0])
            return enums[0].value;
        // 其他的东西
        const { useLorem, useExample, mockArrayItems } = opts || {};
        const loremFn = useLorem && (typeof useLorem === 'function' ? useLorem : defaultLoremFunction);
        if (useExample) {
            if (!isUndefined(schema.example))
                return schema.example;
            if ((_a = schema.examples) === null || _a === void 0 ? void 0 : _a.length)
                return schema.examples[0].value;
        }
        switch (schema.type) {
            case 'string':
                return loremFn ? loremFn(this.schema) : '';
            case 'boolean':
                return loremFn ? loremFn(this.schema) : false;
            case 'integer':
            case 'number':
                return loremFn ? loremFn(this.schema) : 0;
            case 'array': {
                const length = (mockArrayItems && typeof mockArrayItems !== 'number' ? 3 : mockArrayItems) || 0;
                return Array.from({ length }, (_, i) => this.getChildStub(i).getDefaultValue(opts));
            }
            case 'object': {
                const ans = {};
                const { fields, candidates } = this.getFields({ ignoreCondition: opts === null || opts === void 0 ? void 0 : opts.ignoreCondition });
                const populate = (field) => {
                    const stub = this.getChildStub(field.id);
                    if (!stub)
                        return;
                    ans[field.id] = stub.getDefaultValue(opts);
                };
                fields.forEach(populate);
                if (opts === null || opts === void 0 ? void 0 : opts.includeOptional)
                    candidates.forEach(populate);
                return ans;
            }
            case 'null':
            case 'any':
            default:
                return loremFn ? loremFn(this.schema) : null;
        }
    }
    /** 获取枚举项目 */
    getEnums() {
        const { schema } = this;
        if (schema.enum)
            return schema.enum;
        if (schema.enumExpr)
            return tryEvalExpr(schema.enumExpr, null, this, this.exprContext) || null;
        return null;
    }
    /**
     * 安全的获取 title
     *
     * 默认取 schema.title，如果没有的话就取 id，再没有就返回空字符串
     */
    getTitle() {
        if (this.schema.title)
            return this.schema.title;
        if (typeof this.id === 'string')
            return this.id;
        if (typeof this.id === 'number')
            return `数组项#${this.id}`;
        return '';
    }
    /**
     * 获取一个简短的字符串，可大概概括当前的 value 是什么，例如 `[ 3 个元素 ]` 或者 `{ name: xxxx ... }`
     */
    /* istanbul ignore next */
    getSummary() {
        var _a;
        const { value, type } = this;
        if (type === 'array') {
            return `[ ${(_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0} 个${this.arrItems.title || '元素'} ]`;
        }
        if (type === 'object') {
            const firstField = this.getFields().fields.find((field) => /^(?:string|boolean|number|integer)$/.test(field.type));
            if (!firstField)
                return `{ ... }`;
            const id = firstField.id;
            return `{ ${id}: ${String(value === null || value === void 0 ? void 0 : value[id]).substr(0, 32)} ... }`;
        }
        const ans = String(value);
        if (ans.length > 32)
            return `${ans.substr(0, 32)}...`;
        return ans;
    }
    /* istanbul ignore next */
    toString() {
        return `[DataStub @ ${this.path}: ${this.getSummary()}]`;
    }
    // #endregion
    // #region type == object 相关的
    /**
     * 如果当前是一个 object，且有某一个字段的 `isPrimaryKey` 设置为 true，则这里会提供那个字段的 id
     *
     * 可以用于列表渲染等场景
     */
    get primaryKey() {
        var _a;
        if (this.type !== 'object')
            return undefined;
        return (_a = this.definedFields.find((x) => x.required && x.isPrimaryKey)) === null || _a === void 0 ? void 0 : _a.id;
    }
    /**
     * fields 中已经定义的字段（注意，实际中，并不是所有字段都存在！因为有 group 和 condition）
     *
     * 另见：你可能需要的是 `getFields()` 方法
     *
     *  (仅当 type == "object" 时有值)
     */
    get definedFields() {
        return this.schema.fields;
    }
    /**
     * additionalFields 定义的字段 (但是 id 是错的，实际用的时候要处理下)
     *
     * 另见：你可能需要的是 `getFields()` 方法
     *
     *  (仅当 type == "object" 时有值)
     */
    get additionalFields() {
        return this.schema.additionalFields;
    }
    /**
     * 仅当前 type 是 object 时有用。根据当前 value 获取要外显的 fields.
     *
     * 因为有 group 和 additionalFields 的概念存在，部分字段不一定会展示，所以这个和 `definedFields` 会不相同
     *
     * @returns - `fields` 是要外显的字段（对于required，但是值为undefined的字段也会在这里）
     *          - `candidates` 是definedFields定义了但未添加的可选字段
     */
    getFields(opts) {
        /* istanbul ignore next */
        if (this.type !== 'object')
            throw new Error('The schema type of current stub is not object');
        const { value, definedFields, additionalFields } = this;
        let fields = [...definedFields]; // 先把全部已经定义的字段copy过来，哪怕 required: false
        const candidates = [];
        // 如果有字段是 conditional 的，则去掉他们
        if (this.isVolatile && !(opts === null || opts === void 0 ? void 0 : opts.ignoreCondition)) {
            const { groups } = this.schema;
            fields = fields.filter((x) => {
                // 字段本身就定义了条件
                if (typeof x.condition === 'string' && !tryEvalExpr(x.condition, false, this, this.exprContext))
                    return false;
                // group 定义了条件
                const group = !!x.group && groups && groups.find((g) => g.id === x.group);
                if (group && group.condition && !tryEvalExpr(group.condition, false, this, this.exprContext))
                    return false;
                return true;
            });
        }
        const allKeys = new Set((value && typeof value === 'object' && Object.keys(value)) || []);
        // 过滤可选，且未存在的项
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const id = field.id;
            const isInKeys = allKeys.has(id);
            if (!isInKeys && !field.required) {
                // 数据中不存在，而且是可选
                fields.splice(i, 1);
                candidates.push(field);
                i -= 1;
            }
            // 标记 key 已遍历
            if (isInKeys)
                allKeys.delete(id);
        }
        // 执行 additionalFields。此时 allKeys 是剩余的多出来的 key
        if (additionalFields) {
            allKeys.forEach((id) => {
                fields.push(Object.assign(Object.assign({}, additionalFields), { id }));
            });
            allKeys.clear();
        }
        return { fields, candidates };
    }
    /**
     * 修改当前 object 的一个字段，或者更换当前 array 某一个数组元素
     *
     * （仅 object 或者 array 支持）
     */
    /* istanbul ignore next */
    set(fieldId, value) {
        if (this.type !== 'object' && this.type !== 'array')
            throw new Error('Only object or array support this method');
        if (this.type === 'array') {
            // eslint-disable-next-line no-param-reassign
            fieldId = +fieldId;
            if (Number.isNaN(fieldId) || fieldId < 0)
                throw new Error('Index is illegal');
        }
        this.setChildValue(fieldId, value);
    }
    /**
     * 删除一个字段
     *
     * （仅 object 支持）
     */
    delete(fieldId) {
        if (this.type !== 'object')
            throw new Error('Only object support this method');
        const oldVal = this.value;
        // 不存在的字段就跳过
        if (typeof oldVal !== 'object' || oldVal === null || !Object.prototype.hasOwnProperty.call(oldVal, fieldId))
            return;
        const newVal = this.prepareValueForChange(oldVal);
        this.context.delete(newVal, fieldId);
        this.setValue(newVal);
    }
    // #endregion
    // #region type == array 相关的
    /**
     * 数组元素的 schema 信息
     *
     * （仅当 type == "array" 时有值）
     */
    get arrItems() {
        return this.schema.items;
    }
    /**
     * 获取数组长度
     *
     * （仅当 type == "array" 时有值）
     */
    get length() {
        var _a;
        return +((_a = this.value) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    /**
     * 如果要插入一个新的数组项，可以通过这个方法去获取下一个数组项的 默认值
     *
     * （仅当 type == "array" 时能用）
     */
    getArrayItemDefaultValue(opts) {
        var _a, _b;
        return (_b = this.getChildStub(+((_a = this.value) === null || _a === void 0 ? void 0 : _a.length) || 0)) === null || _b === void 0 ? void 0 : _b.getDefaultValue(opts);
    }
    _mutateArray(op) {
        /* istanbul ignore next */
        if (this.type !== 'array')
            throw new Error('Only array can use this method');
        const mutatedTarget = this.prepareValueForChange();
        const ans = op(mutatedTarget);
        this.setValue(mutatedTarget);
        return ans;
    }
    /**
     * （仅当 type == "array" 时能用）
     *
     * 向数组内追加新内容
     */
    push(...items) {
        return this._mutateArray((x) => x.push(...items));
    }
    /**
     * （仅当 type == "array" 时能用）
     *
     * 获取数组最后一个项，然后从数组里删除它
     */
    pop() {
        return this._mutateArray((x) => x.pop());
    }
    /**
     * （仅当 type == "array" 时能用）
     *
     * 获取数组第一个项，然后从数组里删除它
     */
    shift() {
        return this._mutateArray((x) => x.shift());
    }
    splice(...args) {
        /* istanbul ignore next */
        return this._mutateArray((x) => x.splice(...args));
    }
    /**
     * （仅当 type == "array" 时能用）
     *
     * 在数组第一个项前面插入
     */
    unshift(...items) {
        return this._mutateArray((x) => x.unshift(...items));
    }
    // #endregion
    /**
     * 如果你要修改 value，而当前是一个 object 或者 array，你需要考虑 immutable 的问题
     *
     * 在 immutable 模式下，你需要准备一个副本；否则，可直接在原对象上操作
     *
     * 这个就是返回一个你能够用来修改，然后喂给 setValue 的对象。
     *
     * @see setChildValue
     */
    prepareValueForChange(oldValue = this.value) {
        const { immutable } = this.context;
        switch (this.schema.type) {
            case 'array':
                if (!Array.isArray(oldValue))
                    return [];
                return immutable ? [...oldValue] : oldValue;
            case 'object':
                if (typeof oldValue !== 'object' || oldValue === null)
                    return this.getDefaultValue();
                return immutable ? Object.assign({}, oldValue) : oldValue;
            /* istanbul ignore next */
            default:
                return oldValue;
        }
    }
}
_DataStub_objCachedChildStub = new WeakMap();
__decorate([
    LazyGetter()
], DataStub.prototype, "primaryKey", null);
export default DataStub;
